import { useState, FormEvent, useEffect, useCallback } from 'react';
import { useToast } from '../hooks/use-toast';
import { ERR } from '../lib/error-messages';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LayoutDashboard, Mail, ChevronRight } from 'lucide-react';
import { motion } from 'motion/react';
import { useLedgerStore } from '../hooks/use-ledger-store';
import { api } from '../services/api';
import { safeNavigate } from '@/lib/safe-navigate';

const REMEMBERED_ACCOUNTS_KEY = 'rememberedAccounts';
const ADD_NEW_VALUE = '__add_new__';

function decodeBase64UrlToBytes(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const str = atob(padded);
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    bytes[i] = str.charCodeAt(i);
  }
  return bytes.buffer;
}

function encodeBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function getDeviceName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Linux')) return 'Linux';
  return '未知裝置';
}

export default function Login() {
  const { applyAuthSession } = useLedgerStore();
  const { showToast } = useToast();
  const [allowRegistration, setAllowRegistration] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [pendingMagic, setPendingMagic] = useState<{ token: string; email: string; setupPasskey: boolean } | null>(null);
  const [webauthnFailed, setWebauthnFailed] = useState<{ email: string; hasCredentials: boolean } | null>(null);
  const [savedEmails, setSavedEmails] = useState<string[]>([]);
  const [rememberAccount, setRememberAccount] = useState(false);
  const [isAddingNewAccount, setIsAddingNewAccount] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(REMEMBERED_ACCOUNTS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as unknown;
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((e): e is string => typeof e === 'string')) {
          setSavedEmails(parsed);
          setEmail(parsed[0]);
        } else {
          localStorage.removeItem(REMEMBERED_ACCOUNTS_KEY);
        }
      }
    } catch {
      localStorage.removeItem(REMEMBERED_ACCOUNTS_KEY);
    }
  }, []);

  const saveEmailToStorage = useCallback((emailValue: string) => {
    if (!rememberAccount || !emailValue) return;
    setSavedEmails(prev => {
      if (prev.includes(emailValue)) return prev;
      const updated = [...prev, emailValue];
      try {
        localStorage.setItem(REMEMBERED_ACCOUNTS_KEY, JSON.stringify(updated));
      } catch {
        // Storage may be unavailable (e.g. private browsing quota); state is still updated in memory
      }
      return updated;
    });
  }, [rememberAccount]);

  useEffect(() => {
    api.auth.registrationStatus()
      .then((resp) => setAllowRegistration(!!resp?.allowRegistration))
      .catch(() => setAllowRegistration(false));
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const magicToken = url.searchParams.get('magicToken');
    const tokenEmail = url.searchParams.get('email');
    if (!magicToken || !tokenEmail) return;
    const setupPasskey = url.searchParams.get('setupPasskey') === '1';
    if (setupPasskey) {
      setPendingMagic({ token: magicToken, email: tokenEmail, setupPasskey: true });
      setEmail(tokenEmail);
      setHint('可先註冊此裝置 Passkey，或略過直接登入。');
      return;
    }

    const consume = async () => {
      setIsLoading(true);
      setError(null);
      setHint('正在驗證信箱登入連結...');
      try {
        const session = await api.auth.consumeMagicLink({ token: magicToken, email: tokenEmail });
        applyAuthSession(session);
        url.searchParams.delete('magicToken');
        url.searchParams.delete('email');
        window.history.replaceState({}, '', url.toString());
      } catch (err: any) {
        setError(err.message || '登入連結驗證失敗');
      } finally {
        setIsLoading(false);
      }
    };

    consume();
  }, [applyAuthSession]);

  const consumeMagicLink = async (token: string, emailValue: string) => {
    const session = await api.auth.consumeMagicLink({ token, email: emailValue });
    saveEmailToStorage(emailValue);
    applyAuthSession(session);
    const url = new URL(window.location.href);
    url.searchParams.delete('magicToken');
    url.searchParams.delete('email');
    url.searchParams.delete('setupPasskey');
    window.history.replaceState({}, '', url.toString());
  };

  const doWebAuthnLogin = async (
    emailValue: string,
  ): Promise<{ success: boolean; hasCredentials?: boolean; cancelled?: boolean; noLocalAuthenticator?: boolean }> => {
    let optionsResp: any = null;
    try {
      optionsResp = await api.auth.webauthnLoginOptions({ email: emailValue });
      if (!optionsResp?.allowCredentials?.length || !optionsResp?.challenge) {
        return { success: false, hasCredentials: false };
      }
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: decodeBase64UrlToBytes(optionsResp.challenge),
          allowCredentials: optionsResp.allowCredentials.map((c: any) => ({
            id: decodeBase64UrlToBytes(c.id),
            type: 'public-key',
            ...(Array.isArray(c.transports) && c.transports.length > 0 ? { transports: c.transports } : {}),
          })),
          timeout: optionsResp.timeout || 60000,
          userVerification: optionsResp.userVerification || 'preferred',
          rpId: optionsResp.rpId,
        }
      }) as PublicKeyCredential | null;

      if (!assertion) return { success: false, hasCredentials: true, noLocalAuthenticator: true };
      const response = assertion.response as AuthenticatorAssertionResponse;
      const deviceName = getDeviceName();
      const result = await api.auth.webauthnLoginVerify({
        email: emailValue,
        deviceName,
        credential: {
          id: assertion.id,
          rawId: encodeBufferToBase64Url(assertion.rawId),
          type: assertion.type,
          response: {
            clientDataJSON: encodeBufferToBase64Url(response.clientDataJSON),
            authenticatorData: encodeBufferToBase64Url(response.authenticatorData),
            signature: encodeBufferToBase64Url(response.signature),
            userHandle: response.userHandle ? encodeBufferToBase64Url(response.userHandle) : null,
          }
        }
      });
      saveEmailToStorage(emailValue);
      applyAuthSession(result);
      return { success: true };
    } catch (err: any) {
      const name = err?.name as string | undefined;
      const message = String(err?.message || '');
      // Edge／Chrome：本機無對應金鑰時常回 NotAllowedError，應改走信箱登入而非靜默結束
      if (name === 'NotAllowedError' || name === 'AbortError') {
        return {
          success: false,
          hasCredentials: true,
          cancelled: true,
          noLocalAuthenticator: true,
        };
      }
      if (name === 'InvalidStateError' || /no.*credential|not available|沒有/i.test(message)) {
        return { success: false, hasCredentials: true, noLocalAuthenticator: true };
      }
      const hasCredentials = optionsResp?.allowCredentials?.length > 0;
      return { success: false, hasCredentials, noLocalAuthenticator: hasCredentials };
    }
  };

  const handlePasskeyRegistration = async () => {
    if (!email) {
      setError('請先輸入電子郵件');
      return;
    }
    if (!window.PublicKeyCredential) {
      setError('此裝置不支援 WebAuthn');
      return;
    }

    setIsLoading(true);
    setError(null);
    setHint('正在註冊此裝置 Passkey...');

    try {
      // A magic-link visitor must first establish the authenticated session.
      // The server only permits registering a passkey for the current account.
      if (pendingMagic) {
        await consumeMagicLink(pendingMagic.token, pendingMagic.email);
        setPendingMagic(null);
      }
      const options = await api.auth.webauthnRegisterOptions({ email });
      const cred = await navigator.credentials.create({
        publicKey: {
          challenge: decodeBase64UrlToBytes(options.challenge),
          rp: options.rp,
          user: {
            id: decodeBase64UrlToBytes(options.user.id),
            name: options.user.name,
            displayName: options.user.displayName,
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout || 60000,
          authenticatorSelection: options.authenticatorSelection ?? {
            authenticatorAttachment: 'platform',
            residentKey: 'preferred',
            userVerification: 'preferred',
          },
          attestation: (options.attestation ?? 'none') as AttestationConveyancePreference,
          excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
            id: decodeBase64UrlToBytes(c.id),
            type: 'public-key',
          })),
        }
      }) as PublicKeyCredential | null;

      if (!cred) throw new Error('Passkey 註冊失敗');
      console.log('WebAuthn Registration Credential:', cred);

      const attResp = cred.response as AuthenticatorAttestationResponse;
      const deviceName = getDeviceName();
      const session = await api.auth.webauthnRegisterVerify({
        email,
        deviceName,
        credential: {
          id: cred.id,
          rawId: encodeBufferToBase64Url(cred.rawId),
          type: cred.type,
          response: {
            clientDataJSON: encodeBufferToBase64Url(attResp.clientDataJSON),
            attestationObject: encodeBufferToBase64Url(attResp.attestationObject),
            publicKey: attResp.getPublicKey ? (attResp.getPublicKey() ? encodeBufferToBase64Url(attResp.getPublicKey() as ArrayBuffer) : null) : null,
            transports: attResp.getTransports ? attResp.getTransports() : [],
          }
        }
      });
      saveEmailToStorage(email);
      applyAuthSession(session);
      setWebauthnFailed(null);
    } catch (err: any) {
      showToast(err.message || ERR.AUTH_PASSKEY_REGISTER_FAILED, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 功能：寄送免密碼登入信並更新提示文案
   * @param {string} emailValue 目標 email
   * @returns {Promise<void>}
   */
  const sendPasswordlessLoginEmail = async (emailValue: string): Promise<void> => {
    const resp = await api.auth.requestPasswordless({ email: emailValue });
    if (resp?.fallbackLink) {
      setHint(`寄信功能未設定，請直接開啟連結完成登入：${resp.fallbackLink}`);
    } else {
      setHint('若該信箱可收信，系統已寄出登入連結。3 分鐘內不可重複寄送。');
    }
    setWebauthnFailed(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setHint(null);
    setWebauthnFailed(null);

    try {
      if (!window.PublicKeyCredential) {
        setWebauthnFailed({ email, hasCredentials: false });
        setHint('此裝置不支援 Passkey，請點下方「Email 登入」。');
        return;
      }

      const result = await doWebAuthnLogin(email);
      if (result.success) {
        return;
      }

      // Passkey 失敗／取消／本機無金鑰 → 顯示 Email 登入按鈕（需使用者手動點擊才寄信）
      setWebauthnFailed({ email, hasCredentials: !!result.hasCredentials });
      setHint('此裝置沒有可用的 Passkey，請點下方「Email 登入」。');
    } catch (err: any) {
      showToast(err.message || ERR.GENERIC, 'error');
      setWebauthnFailed({ email, hasCredentials: true });
      setHint('Passkey 登入失敗，請點下方「Email 登入」。');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 font-sans">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[60%] h-[60%] bg-primary/10 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-secondary/10 rounded-full blur-[120px]"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-6 shadow-wash">
            <LayoutDashboard size={32} />
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-foreground mb-2">
            免密碼登入
          </h1>
          <p className="text-muted-foreground font-medium">
            輸入信箱自動驗證
          </p>
        </div>

        <Card className="bg-muted/30 border-border/50 rounded-[2.5rem] p-8 shadow-wash backdrop-blur-xl border border-white/5">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-widest ml-1">電子郵件</Label>
              {savedEmails.length > 0 && !isAddingNewAccount ? (
                <Select
                  value={email}
                  onValueChange={(value) => {
                    if (value === ADD_NEW_VALUE) {
                      setIsAddingNewAccount(true);
                      setEmail('');
                    } else {
                      setEmail(value);
                    }
                  }}
                >
                  <SelectTrigger className="bg-muted border-transparent focus:border-primary/50 rounded-2xl h-14 pl-4 w-full text-base">
                    <div className="flex items-center gap-3 flex-1">
                      <Mail className="text-muted-foreground shrink-0" size={18} />
                      <SelectValue placeholder="選擇帳號" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {savedEmails.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                    <SelectItem value={ADD_NEW_VALUE} className="text-primary font-bold">
                      ＋ 新增帳號
                    </SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      type="email"
                      placeholder="name@example.com"
                      className="bg-muted border-transparent focus:border-primary/50 focus:ring-primary/20 rounded-2xl h-14 pl-12"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <CheckboxRow
                    checked={rememberAccount}
                    onCheckedChange={(checked) => setRememberAccount(checked)}
                    className="ml-1 mt-2 gap-2 w-auto justify-start"
                  >
                    <Label className="text-sm font-medium text-muted-foreground cursor-pointer select-none pointer-events-none">
                      記住帳號
                    </Label>
                  </CheckboxRow>
                </>
              )}
            </div>

            {error && <p className="text-destructive text-sm font-bold text-center">{error}</p>}
            {hint && <p className="text-primary text-sm font-bold text-center break-all">{hint}</p>}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-14 rounded-2xl bg-gradient-to-r from-primary to-accent text-primary-foreground font-black text-lg shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
              ) : (
                '登入系統'
              )}
            </Button>
            {pendingMagic?.setupPasskey && (
              <div className="space-y-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  className="w-full h-12 rounded-2xl border-border hover:bg-muted font-bold"
                  onClick={handlePasskeyRegistration}
                >
                  註冊此裝置 Passkey 後登入
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isLoading}
                  className="w-full h-12 rounded-2xl font-bold"
                  onClick={async () => {
                    if (!pendingMagic) return;
                    setIsLoading(true);
                    try {
                      await consumeMagicLink(pendingMagic.token, pendingMagic.email);
                    } catch (err: any) {
                      showToast(err.message || ERR.AUTH_LOGIN_FAILED, 'error');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  略過 Passkey，直接登入
                </Button>
              </div>
            )}
            {webauthnFailed && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-center text-muted-foreground">
                  此裝置無法使用 Passkey，請改用 Email 登入：
                </p>
                <Button
                  type="button"
                  disabled={isLoading}
                  className="w-full h-14 rounded-2xl bg-secondary text-secondary-foreground font-black text-lg shadow-lg hover:shadow-xl active:scale-[0.98] transition-all"
                  onClick={async () => {
                    setIsLoading(true);
                    setError(null);
                    setHint(null);
                    try {
                      await sendPasswordlessLoginEmail(webauthnFailed.email);
                    } catch (err: any) {
                      showToast(err.message || ERR.AUTH_SEND_FAILED, 'error');
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  {isLoading ? (
                    <div className="w-6 h-6 border-2 border-secondary-foreground/30 border-t-secondary-foreground rounded-full animate-spin"></div>
                  ) : (
                    'Email 登入'
                  )}
                </Button>
              </div>
            )}
          </form>

          {allowRegistration && (
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-center text-sm text-muted-foreground mb-4 font-medium">還沒有帳號？</p>
              <Button
                variant="outline"
                className="w-full h-12 rounded-2xl border-border hover:bg-muted font-bold flex items-center justify-between"
                onClick={() => safeNavigate('/register')}
              >
                <span>建立新帳號</span>
                <ChevronRight size={18} />
              </Button>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
